// @flow
import {
  CircularProgress,
  Hidden,
  MenuItem,
  Typography,
  makeStyles,
} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import CustomButton from '../../../../../../elements/CustomButton/CustomButton';
import FacebookIcon from '../../../../../../elements/icons/FacebookIcon';
import InstagramIcon from '../../../../../../elements/icons/InstagramIcon';
import WhatsAppIcon from '../../../../../../elements/icons/WhatsAppIcon';
import {colors} from '../../../../../../assets/styles/colors';

import {useHistory} from 'react-router-dom';
import CustomModal from '../../../../../../elements/CustomModal/CustomModal';
import CustomSelect from '../../../../../../elements/CustomSelect/CustomSelect';
import {useCreateMetaAccount} from '../../../utils';
import {useStoreInfo} from '../../../../../store/utils';
import {
  connectFbMessengerDeviceService,
  connectInstagramDeviceService,
  connectWhatsappApiDeviceService,
} from '../../../promises';
import env from '../../../../../../environment';
import InlineLoader from '../../../../../../elements/LoadingSpinner/InlineLoader';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    borderRadius: '.75rem',
    gap: '1rem',
    maxWidth: '416px',
    margin: '0 auto',
    position: 'relative',

    [theme.breakpoints.up('sm')]: {
      paddingBlock: '2rem',
    },
  },
  header: {
    padding: '1rem',
    borderBottom: `1px solid ${colors.grey200}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '.5rem',
  },
  loader: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    zIndex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
  },
  title: {
    color: colors.grey800,
    fontWeight: 600,
    textAlign: 'center',
    fontSize: '1rem',
  },
  textInfo: {
    fontSize: '.8rem',
    color: colors.grey600,
  },
  content: {
    paddingTop: '.5rem',
    padding: '1rem',
    display: 'flex',
    width: '100%',
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: '.5rem',
  },
  connectButton: {
    display: 'flex',
    color: 'white',
    borderRadius: '.25rem',
    padding: '.6rem',

    '&[data-type="ig"]': {
      background:
        'radial-gradient(19519.74% 280.41% at -50.85% 198.75%, #FD5 14.46%, #FF543E 39.22%, #C837AB 64.62%, #5953D1 98.24%), #1877F2',
      '&:hover': {
        background:
          'radial-gradient(19519.74% 280.41% at -50.85% 198.75%, #FD5 14.46%, #FF543E 39.22%, #C837AB 64.62%, #5953D1 98.24%), #1877F2',
        opacity: 0.8,
      },
    },

    '&[data-type="fb"]': {
      background: '#2D69EC',
      '&:hover': {
        background: '#2D69EC',
        opacity: 0.8,
      },
    },

    '&[data-type="wb"]': {
      background: '#25D366',
      '&:hover': {
        background: '#25D366',
        opacity: 0.8,
      },
    },
  },
  connectButtonLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    gap: '.5rem',
    position: 'relative',
    color: 'white',
  },
  connectButtonIcon: {
    fontSize: '1.5rem',
    position: 'absolute',
    left: '.5rem',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  connectButtonText: {
    fontSize: '.875rem',
    fontWeight: 600,
    textAlign: 'center',
    color: 'white',
  },
  continueButton: {
    display: 'flex',
    gap: '.5rem',
    alignItems: 'center',
  },
}));

const FACEBOOK_APP_ID = env.metaAppId;

const FACEBOOK_LOGIN_URL = 'https://www.facebook.com/v21.0/dialog/oauth';

export const getRedirectUri = () => {
  if (env.environment === 'production') {
    return 'https://tiendada.com/redirect/facebook-login';
  }

  if (env.environment === 'development') {
    return 'https://dev.tiendada.com/redirect/facebook-login';
  }

  return 'https://local.tiendada.com/redirect/facebook-login';
};

const getLoginUrl = (type: 'ig' | 'fb' | 'wb', storeId: string) => {
  let redirectPath = '/chat/configuration/instagram';

  if (type === 'fb') {
    redirectPath = '/chat/configuration/messenger';
  }
  if (type === 'ig') {
    redirectPath = '/chat/configuration/instagram';
  }
  if (type === 'wb') {
    redirectPath = '/chat/configuration/whatsapp-api';
  }

  const loginUrl = new URL(FACEBOOK_LOGIN_URL);

  const stateParam = {
    metaAppId: FACEBOOK_APP_ID,
    path: redirectPath,
    storeId,
  };

  const stateB64 = window.btoa(JSON.stringify(stateParam));

  const REDIRECT_URI = getRedirectUri();

  loginUrl.searchParams.append('client_id', FACEBOOK_APP_ID);
  loginUrl.searchParams.append('redirect_uri', REDIRECT_URI);
  loginUrl.searchParams.append('state', stateB64);

  if (type === 'ig') {
    /*loginUrl.searchParams.append(
      'scope',
      'business_management,instagram_basic,instagram_manage_messages,pages_manage_metadata,pages_messaging,pages_show_list,instagram_manage_comments'
    );*/
    loginUrl.searchParams.append('config_id', env.instagramLoginConfigId);
  }

  if (type === 'fb') {
    loginUrl.searchParams.append(
      'scope',
      'business_management,pages_manage_metadata,pages_messaging,pages_show_list'
    );
  }

  if (type === 'wb') {
    /*loginUrl.searchParams.append(
      'scope',
      'business_management,whatsapp_business_management,whatsapp_business_messaging,pages_show_list'
    );*/
    loginUrl.searchParams.append('config_id', env.whatsappApiConfigId);
  }

  loginUrl.searchParams.append('response_type', 'code');
  loginUrl.searchParams.append('display', 'page');

  if (type === 'ig') {
    loginUrl.searchParams.append(
      'extras',
      JSON.stringify({
        setup: {
          channel: 'IG_API_ONBOARDING',
        },
      })
    );
  }

  if (type === 'wb') {
    loginUrl.searchParams.append(
      'extras',
      JSON.stringify({
        setup: {},
        featureType: '',
        sessionInfoVersion: '2',
      })
    );
  }

  return loginUrl.toString();
};

type Props = {
  type: 'ig' | 'fb' | 'wb',
};

export default function FbIgConfig({type}: Props) {
  const classes = useStyles();

  const [deviceSelected, setDeviceSelected] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const storeInfo = useStoreInfo();

  const {
    metaAccounts,
    metaUser,
    whatsappApiAccounts,
    loading: gettingMetaAccount,
  } = useCreateMetaAccount(type);

  const [connectedAccount, setConnectedAccount] = useState('');
  const history = useHistory();

  useEffect(() => {
    if (metaAccounts.length === 0) return;
    if (metaAccounts.length === 1) {
      setDeviceSelected(metaAccounts[0].id);
    }
  }, [metaAccounts]);

  useEffect(() => {
    if (!metaUser) return;
    setConnectedAccount(metaUser.name);
  }, [metaUser]);

  if (!storeInfo) return null;

  const handleChooseSocial = () => {
    history.push('/chat/configuration');
  };

  let socialLabel = '';
  let socialIcon = null;

  if (type === 'ig') {
    socialLabel = 'Instagram';
    socialIcon = <InstagramIcon />;
  } else if (type === 'fb') {
    socialLabel = 'Facebook';
    socialIcon = <FacebookIcon />;
  } else if (type === 'wb') {
    socialLabel = 'WhatsApp Api';
    socialIcon = <WhatsAppIcon />;
  }

  const loginUrl = getLoginUrl(type, storeInfo.id);

  const handleConnectPage = () => {
    if (type === 'wb') {
      setLoading(true);
      connectWhatsappApiDeviceService(deviceSelected)
        .then(() => {
          history.push('/chat/multi-chat');
        })
        .finally(() => {
          setLoading(false);
        });
      return;
    }

    if (!metaUser) return;
    setLoading(true);

    const facebookMetaUserId = metaUser.id;
    if (type === 'ig') {
      connectInstagramDeviceService({
        facebookMetaUserId,
        instagramAccountId: deviceSelected,
      })
        .then(() => {
          history.push('/chat/multi-chat');
        })
        .finally(() => {
          setLoading(false);
        });
      return;
    }

    if (type === 'fb') {
      connectFbMessengerDeviceService({
        facebookMetaUserId,
        fbMessengerAccountId: deviceSelected,
      })
        .then(() => {
          history.push('/chat/multi-chat');
        })
        .finally(() => {
          setLoading(false);
        });
      return;
    }
  };

  const content = (
    <div className={classes.root}>
      {gettingMetaAccount && (
        <div className={classes.loader}>
          <InlineLoader loading />
        </div>
      )}
      <header className={classes.header}>
        <Typography className={classes.title}>
          Conecta con {socialLabel}
        </Typography>
        <Typography className={classes.textInfo}>
          Esta integración permite la comunicación con tus clientes de{' '}
          {socialLabel}.
        </Typography>
      </header>

      <div className={classes.content}>
        <Typography className={classes.textInfo}>
          1. Permite el acceso a los mensajes en tu configuración.
        </Typography>
        <Typography className={classes.textInfo}>
          2. Presiona "Conectar con {socialLabel}". Es posible que debas iniciar
          sesión.
        </Typography>

        {type !== 'wb' && (
          <CustomButton
            className={classes.connectButton}
            fullWidth
            href={loginUrl || undefined}
            target="_self"
            rel="noreferrer"
            data-type={type}
            disabled={!!connectedAccount}
          >
            <span className={classes.connectButtonLabel}>
              <span className={classes.connectButtonIcon}>{socialIcon}</span>
              <span className={classes.connectButtonText}>
                {connectedAccount
                  ? `Conectado con ${connectedAccount}`
                  : `Conectar con ${socialLabel}`}
              </span>
            </span>
          </CustomButton>
        )}

        {type === 'wb' ? (
          <>
            <div style={{display: 'flex', width: '100%'}}>
              <CustomButton
                className={classes.connectButton}
                fullWidth
                href={loginUrl || undefined}
                target="_self"
                rel="noreferrer"
                data-type={type}
                disabled={whatsappApiAccounts.length > 0}
              >
                <span className={classes.connectButtonLabel}>
                  <span className={classes.connectButtonIcon}>
                    {socialIcon}
                  </span>
                  <span className={classes.connectButtonText}>
                    Conectar con {socialLabel}
                  </span>
                </span>
              </CustomButton>
            </div>

            <Typography
              className={classes.textInfo}
              style={{
                fontWeight:
                  whatsappApiAccounts.length === 0 ? 'bold' : 'normal',
              }}
            >
              3. Selecciona la cuenta de WhatsApp que deseas conectar.
            </Typography>

            <CustomSelect
              fullWidth
              onChange={e => {
                setDeviceSelected(e.target.value);
              }}
              value={deviceSelected}
              disabled={whatsappApiAccounts.length === 0}
            >
              {whatsappApiAccounts.map(account => (
                <MenuItem key={account.id} value={account.id}>
                  {account.phone}
                </MenuItem>
              ))}
            </CustomSelect>

            <CustomButton
              variant="contained"
              color="primary"
              fullWidth
              disabled={!deviceSelected || loading}
              onClick={handleConnectPage}
            >
              <div className={classes.continueButton}>
                {loading && <CircularProgress size={20} />}
                <span>Conectar cuenta</span>
              </div>
            </CustomButton>
          </>
        ) : (
          <>
            <Typography className={classes.textInfo}>
              3. Selecciona la cuenta de {socialLabel} que deseas conectar.
            </Typography>

            <CustomSelect
              fullWidth
              onChange={e => {
                setDeviceSelected(e.target.value);
              }}
              value={deviceSelected}
              disabled={metaAccounts.length === 0}
            >
              {metaAccounts.map(account => (
                <MenuItem key={account.id} value={account.id}>
                  {account.name}
                </MenuItem>
              ))}
            </CustomSelect>

            <CustomButton
              variant="contained"
              color="primary"
              fullWidth
              disabled={!deviceSelected || loading}
              onClick={handleConnectPage}
            >
              <div className={classes.continueButton}>
                {loading && <CircularProgress size={20} />}
                <span>Conectar cuenta</span>
              </div>
            </CustomButton>
          </>
        )}
      </div>
    </div>
  );

  return (
    <>
      <Hidden mdUp>{content}</Hidden>
      <Hidden smDown>
        <CustomModal
          open
          onClose={() => {
            handleChooseSocial();
          }}
        >
          {content}
        </CustomModal>
      </Hidden>
    </>
  );
}
